import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { Button } from "@/shared/ui/button";
import { SentryUserContextSync } from "@/integrations/sentry-user-context";
import { SentryFeedbackButton } from "@/integrations/sentry-feedback-button";
import { ErrorFallback } from "@/integrations/sentry-error-fallback";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import WorkInstructions from "@/pages/WorkInstructions";
import InstructionDetailPage from "@/pages/InstructionDetailPage";
import { AuthProvider } from "@/core/AuthContext";
import { LanguageProvider } from "@/core/LanguageContext";
import { AppLayout } from "@/core/components/layout/AppLayout";
import Index from "@/pages/Index";
import ModulePage from "@/pages/ModulePage";
import NotFound from "@/pages/NotFound";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import PasswordReset from "@/pages/PasswordReset";
import EmailConfirmation from "@/pages/EmailConfirmation";
import CheckEmail from "@/pages/CheckEmail";
import NoAccess from "@/pages/NoAccess";
import JoinFactory from "@/pages/JoinFactory";
import DebugErrors from "@/pages/DebugErrors";
import UserManagement from "@/pages/admin/UserManagement";
import FactoryStructure from "@/pages/admin/FactoryStructure";
import UNSMapper from "@/pages/admin/UNSMapper";
import SuperAdminDashboard from "@/modules/superadmin/pages/SuperAdminDashboard";
import PerformancePage from "@/pages/PerformancePage";
import EightDManagement from "@/modules/8d/pages/EightDManagement";
import SupplierClaimView from "@/modules/8d/pages/SupplierClaimView";
import AIProductionControl from "@/modules/ai/pages/AIProductionControl";
import EnergyManagement from "@/modules/energy/pages/EnergyManagement";
import LpaManagement from "@/modules/lpa/pages/LpaManagement";
import AuditDetailPage from "@/modules/lpa/components/AuditDetailPage";
import ScheduleDetailPage from "@/modules/lpa/components/ScheduleDetailPage";
import DocumentAssistant from "@/pages/DocumentAssistant";
import CompetencyManagement from "@/pages/CompetencyManagement";
import Profile from "@/pages/Profile";
import SupplierQuality from "@/pages/SupplierQuality";
import SupplierQualityDetail from "@/pages/SupplierQualityDetail";
import SupplierInspectionPortal from "@/pages/SupplierInspectionPortal";
import FactoryDashboard from "@/pages/FactoryDashboard";
import { ScrapEntryPage } from "@/pages/ScrapEntryPage";
import { CustomerComplaintsPage } from "@/pages/CustomerComplaintsPage";
import { CustomersPage } from "@/pages/CustomersPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Consider data fresh for 30 seconds — prevents every query from
      // refetching simultaneously when the browser tab regains focus,
      // which otherwise causes cascading re-renders that can unmount pages.
      staleTime: 30 * 1000,
    },
  },
});
const shouldShowSentryTestButton =
  import.meta.env.DEV && import.meta.env.VITE_SHOW_SENTRY_TEST_BUTTON === "true";

const ErrorButton = () => (
  <Button
    type="button"
    variant="destructive"
    size="sm"
    className="fixed bottom-16 right-4 z-[1000]"
    onClick={() => {
      throw new Error("This is your first error!");
    }}
  >
    Break the world
  </Button>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SentryUserContextSync />
      <LanguageProvider>
        <TooltipProvider>
          <Sentry.ErrorBoundary fallback={(fallbackProps) => <ErrorFallback {...fallbackProps} />}>
            <Toaster />
            <Sonner />
            <SentryFeedbackButton />
            <BrowserRouter>
              <Routes>
                {/* Public routes (no auth required) */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route path="/check-email" element={<CheckEmail />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/no-access" element={<NoAccess />} />
                <Route path="/debug-errors" element={<DebugErrors />} />
                <Route path="/8d/supplier/:claimId" element={<SupplierClaimView />} />
                <Route path="/supplier-inspection/:token" element={<SupplierInspectionPortal />} />
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/factory-dashboard" element={<FactoryDashboard />} />
                  <Route path="/startup" element={<Navigate to="/instructions" replace />} />
                  <Route path="/instructions" element={<WorkInstructions />} />
                  <Route path="/instructions/:instructionId" element={<InstructionDetailPage />} />
                  <Route path="/quality-checks" element={<ModulePage />} />
                  <Route path="/defects" element={<ModulePage />} />
                  <Route path="/shift-reports" element={<ModulePage />} />
                  <Route path="/performance" element={<PerformancePage />} />
                  <Route path="/competency" element={<CompetencyManagement />} />
                  <Route path="/8d-management" element={<EightDManagement />} />
                  <Route path="/supplier-quality" element={<SupplierQuality />} />
                  <Route path="/supplier-quality/:supplierId" element={<SupplierQualityDetail />} />
                  <Route path="/audits" element={<Navigate to="/lpa" replace />} />
                  <Route path="/lpa" element={<LpaManagement />} />
                  <Route path="/lpa/audits/:auditId" element={<AuditDetailPage />} />
                  <Route path="/lpa/audits/:auditId/schedules/:scheduleId" element={<ScheduleDetailPage />} />
                  <Route path="/energy" element={<EnergyManagement />} />
                  <Route path="/document-assistant" element={<DocumentAssistant />} />
                  <Route path="/scrap-entry" element={<ScrapEntryPage />} />
                  <Route path="/customer-complaints" element={<CustomerComplaintsPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/ai-control" element={<AIProductionControl />} />
                  <Route path="/join-factory" element={<JoinFactory />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                  <Route path="/superadmin" element={<SuperAdminDashboard />} />
                  <Route path="/admin/factory-structure" element={<FactoryStructure />} />
                  <Route path="/admin/uns-mapper" element={<UNSMapper />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            {shouldShowSentryTestButton ? <ErrorButton /> : null}
          </Sentry.ErrorBoundary>
        </TooltipProvider>
      </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
